/**
 * Reine Hilfsfunktionen der Strategie-Kandidaten: Shortlist-Auswahl, Datenalter, Datenhinweise,
 * Peer-Perzentil und Prüfbericht. Alte Läufe liefern diese Felder nicht, deshalb verträgt jede
 * Funktion fehlende Werte.
 */

import { DASH } from './opportunities/opportunityFormat';

export const VIEW_SHORTLIST = 'shortlist';
export const VIEW_ALL = 'all';

export const GATE_REASON_LABELS = {
    kurs_veraltet: 'Kurs veraltet',
    fundamentals_veraltet: 'Fundamentaldaten zu alt',
    cedear: 'CEDEAR',
    lse_auslandslisting: 'LSE-Auslandslisting',
    fonds: 'Fonds/Investment-Trust',
    marktkapitalisierung: 'Marktkapitalisierung zu klein',
    volumen: 'Handelsvolumen zu gering',
    handelsumsatz: 'Handelsumsatz zu gering',
    fx_fehlt: 'Wechselkurs fehlt',
    dublette: 'Mehrfachlisting',
    KURSZIEL_VERWORFEN: 'Kursziel verworfen',
    KGV_VERWORFEN: 'KGV verworfen',
    WACHSTUM_VERWORFEN: 'Wachstum verworfen',
    WERT_VERWORFEN: 'Wert verworfen',
};

/** Die Datenbank liefert NUMERIC als Text. Nur echte Zahlen kommen durch, null bleibt null. */
export function toNumber(value) {
    if (value == null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

export function normalizeViewMode(value) {
    return value === VIEW_ALL ? VIEW_ALL : VIEW_SHORTLIST;
}

/**
 * Ohne ein einziges Shortlist-Papier gibt es nichts auszuwählen. Das gilt für alte Läufe, bei denen
 * das Feld fehlt, und ebenso, wenn sie es nachträglich überall mit false oder null bekommen.
 */
export function hasShortlist(candidates) {
    return (candidates || []).some(c => c.is_shortlist === true);
}

/** Aufsteigend nach Rang. Papiere ohne Rang stehen hinten. */
export function compareShortlistRank(a, b) {
    const rankA = toNumber(a);
    const rankB = toNumber(b);
    if (rankA == null && rankB == null) return 0;
    if (rankA == null) return 1;
    if (rankB == null) return -1;
    return rankA - rankB;
}

/** Die in der gewählten Ansicht sichtbaren Kandidaten. Ohne Shortlist immer alle. */
export function selectViewCandidates(candidates, viewMode) {
    const rows = candidates || [];
    if (viewMode !== VIEW_SHORTLIST || !hasShortlist(rows)) return rows;
    return rows
        .filter(c => c.is_shortlist === true)
        .sort((a, b) => compareShortlistRank(a.shortlist_rank, b.shortlist_rank));
}

/** Quartal des Fundamentaldatums, z. B. „Q3/26“. Reine Tagesangaben wandern nicht über die Zeitzone. */
export function fmtQuarter(value) {
    if (!value) return DASH;
    const text = String(value);
    const day = /^(\d{4})-(\d{2})-\d{2}$/.exec(text);
    let year;
    let month;
    if (day) {
        year = Number(day[1]);
        month = Number(day[2]);
    } else {
        const parsed = new Date(text);
        if (Number.isNaN(parsed.getTime())) return DASH;
        year = parsed.getFullYear();
        month = parsed.getMonth() + 1;
    }
    if (month < 1 || month > 12) return DASH;
    return `Q${Math.ceil(month / 3)}/${String(year).slice(-2)}`;
}

/** Texte der Datenhinweise. Ohne „message“ bleibt der Code, damit kein Hinweis verloren geht. */
export function qualityMessages(flags) {
    if (!Array.isArray(flags)) return [];
    return flags.map(flag => flag?.message || flag?.code).filter(Boolean);
}

/** Aus dem Rang-Perzentil (höher ist besser) wird der „Top X %“-Wert, mindestens 1. */
export function peerTopPercent(peerRankPct) {
    const pct = toNumber(peerRankPct);
    if (pct == null) return null;
    return Math.min(100, Math.max(1, Math.round(100 - pct)));
}

export function fmtPeerTop(peerRankPct) {
    const top = peerTopPercent(peerRankPct);
    return top == null ? DASH : `Top ${top} %`;
}

export function gateReasonLabel(code) {
    return GATE_REASON_LABELS[code] || code;
}

/** Gründe mit Anzahl, die häufigsten zuerst. Gründe ohne Treffer entfallen. */
export function gateEntries(reasons) {
    if (!reasons || typeof reasons !== 'object') return [];
    return Object.entries(reasons)
        .map(([code, count]) => ({ code, label: gateReasonLabel(code), count: toNumber(count) }))
        .filter(entry => entry.count > 0)
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'de'));
}

/** Aufbereiteter Prüfbericht oder null, wenn der Lauf keinen vollständigen Bericht hat. */
export function parseGateReport(report) {
    if (!report || typeof report !== 'object') return null;
    const before = toNumber(report.universe_before);
    const after = toNumber(report.universe_after);
    if (before == null || after == null) return null;
    return {
        before,
        after,
        excluded: gateEntries(report.excluded),
        voided: gateEntries(report.voided),
        scoringErrors: toNumber(report.scoring_errors) || 0,
    };
}
